/* Shared dashboard utility components */
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

export function PageContainer({ children, className = "", variant = "normal", ...props }) {
  const width = variant === "wide" ? "w-full" : "w-full max-w-[1440px] mx-auto";
  return (
    <div className={`${width} px-4 sm:px-5 md:px-5 lg:px-6 xl:px-7 pt-4 md:pt-5 pb-8 ${className}`} {...props}>
      {children}
    </div>
  );
}

// Cockpit (Direction 2) typography — Barlow Condensed BLACK uppercase for
// every page header inside the dark office shell. Mobile keeps the compact
// iOS-style top bar.
const FONT_HEAD = { fontFamily: "'Barlow Condensed', system-ui, sans-serif" };

// Responsive PageHeader.
// Mobile (< md): iOS-style — sticky, backdrop-blurred, compact title.
// Desktop (md+): cockpit slashy eyebrow + huge Barlow Condensed display title.
//
// IMPORTANT: this component renders `action` EXACTLY ONCE. An earlier
// implementation rendered mobile and desktop subtrees as siblings (md:hidden
// + hidden md:flex), which duplicated `action`. When the action was a
// <Dialog>, two Dialog instances would mount and Radix portals stacked two
// copies of DialogContent — see /app/test_reports/iteration_33.json for the
// full RCA. Don't reintroduce the duplication.
export function PageHeader({ tag, eyebrow, title, subtitle, action, back }) {
  const eb = eyebrow || tag;
  // Strip leading slashes/dashes from the eyebrow then re-prefix with the
  // cockpit `//` motif so existing pages get the new style automatically.
  const cleanEb = eb ? String(eb).replace(/^[/\s]+/, "").trim() : null;
  return (
    <div
      className={[
        "dashboard-page-header",
        "mb-4 md:mb-8",
        "-mx-4 sm:-mx-6 md:mx-0",
        "sticky md:static top-0 z-30 md:z-auto",
        "bg-white/85 md:bg-transparent backdrop-blur-xl md:backdrop-blur-0",
        "border-b md:border-b-0 border-zinc-200 dark:border-zinc-800",
        "dark:bg-[#101620]/85 md:dark:bg-transparent",
      ].join(" ")}
    >
      <div className="px-4 md:px-0 py-3 md:py-0 flex flex-wrap items-center md:items-end justify-between gap-2 md:gap-4">
        <div className="flex items-center md:items-start gap-2 md:gap-0 flex-1 min-w-[55%] md:min-w-0">
          {back && (
            <Link
              to={back}
              className="md:hidden -ml-1 p-2 rounded-full active:bg-zinc-100 dark:active:bg-zinc-800 shrink-0"
              aria-label="Go back"
              data-testid="page-back"
            >
              <span className="block w-2.5 h-2.5 border-l-2 border-b-2 border-zinc-900 dark:border-white rotate-45" />
            </Link>
          )}
          <div className="flex-1 min-w-0">
            {cleanEb && (
              <div
                className="hidden md:block text-[10px] uppercase tracking-[0.22em] text-[#FF5F15] mb-2"
                style={{ ...FONT_HEAD, fontWeight: 700 }}
              >
                // {cleanEb}
              </div>
            )}
            <h1
              className={[
                "text-lg font-bold tracking-tight text-zinc-900 dark:text-white truncate",
                "md:text-4xl md:font-extrabold md:uppercase md:tracking-tight",
                "md:whitespace-normal md:overflow-visible md:text-inherit",
                "md:font-display",
              ].join(" ")}
            >
              {title}
            </h1>
            {subtitle && (
              <p
                className={[
                  "text-[11px] text-zinc-500 dark:text-zinc-400 truncate",
                  "md:text-sm md:text-zinc-600 dark:md:text-zinc-400 md:mt-2 md:max-w-2xl",
                  "md:whitespace-normal md:overflow-visible",
                ].join(" ")}
              >
                {subtitle}
              </p>
            )}
          </div>
        </div>
        {action && <div className="min-w-0 max-w-full">{action}</div>}
      </div>
    </div>
  );
}

export function Stat({ label, value, suffix, accent }) {
  return (
    <div className="hard-card p-5">
      <div
        className="text-[10px] uppercase tracking-[0.22em] text-[#FF5F15]"
        style={{ ...FONT_HEAD, fontWeight: 700 }}
      >
        // {label}
      </div>
      <div className="mt-3 flex items-end gap-2">
        <span
          className="text-3xl md:text-4xl tracking-tight text-zinc-900 dark:text-white"
          style={{ ...FONT_HEAD, fontWeight: 800 }}
        >
          {value}
        </span>
        {suffix && (
          <span
            className="text-[11px] mb-1.5 uppercase tracking-[0.14em] text-zinc-500 dark:text-zinc-400"
            style={{ ...FONT_HEAD, fontWeight: 700 }}
          >
            {suffix}
          </span>
        )}
      </div>
      {accent && <div className={`mt-3 h-1 w-10 ${accent}`} />}
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    pending: "bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-500/15 dark:text-yellow-300 dark:border-yellow-700/50",
    approved: "bg-green-100 text-green-800 border-green-300 dark:bg-green-500/15 dark:text-green-300 dark:border-green-700/50",
    rejected: "bg-red-100 text-red-800 border-red-300 dark:bg-red-500/15 dark:text-red-300 dark:border-red-700/50",
    open: "bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-500/15 dark:text-orange-300 dark:border-orange-700/50",
    closed: "bg-zinc-200 text-zinc-700 border-zinc-300 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700",
    planned: "bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-700/50",
    in_progress: "bg-[#FF5F15] text-white border-[#FF5F15]",
    on_hold: "bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-500/15 dark:text-yellow-300 dark:border-yellow-700/50",
    completed: "bg-green-100 text-green-800 border-green-300 dark:bg-green-500/15 dark:text-green-300 dark:border-green-700/50",
    active: "bg-green-100 text-green-800 border-green-300 dark:bg-green-500/15 dark:text-green-300 dark:border-green-700/50",
    maintenance: "bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-500/15 dark:text-yellow-300 dark:border-yellow-700/50",
    retired: "bg-zinc-200 text-zinc-700 border-zinc-300 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700",
    low: "bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-700/50",
    medium: "bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-500/15 dark:text-yellow-300 dark:border-yellow-700/50",
    high: "bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-500/15 dark:text-orange-300 dark:border-orange-700/50",
    critical: "bg-red-600 text-white border-red-600",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] border rounded-sm ${map[status] || "bg-zinc-100 text-zinc-700 border-zinc-300 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700"}`}
      style={{ ...FONT_HEAD, fontWeight: 700 }}
    >
      {String(status).replace("_", " ")}
    </span>
  );
}

export function EmptyState({ title, description, action, icon: Icon }) {
  return (
    <>
      {/* MOBILE — clean, no dashed border, more friendly */}
      <div className="md:hidden py-12 px-4 text-center" data-testid="empty-state">
        {Icon && (
          <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
            <Icon className="w-7 h-7 text-zinc-400 dark:text-zinc-500" />
          </div>
        )}
        <div className="text-lg font-bold tracking-tight text-zinc-900 dark:text-white">{title}</div>
        {description && <p className="text-zinc-500 dark:text-zinc-400 mt-1.5 text-sm max-w-xs mx-auto leading-relaxed">{description}</p>}
        {action && <div className="mt-5 flex justify-center">{action}</div>}
      </div>
      {/* DESKTOP — compact cockpit empty row */}
      <div className="hidden md:flex hard-card px-6 py-5 items-center justify-between gap-4" data-testid="empty-state">
        <div className="min-w-0">
          <div
            className="text-lg uppercase tracking-tight text-zinc-900 dark:text-white"
            style={{ ...FONT_HEAD, fontWeight: 800 }}
          >
            // {title}
          </div>
          {description && <p className="text-zinc-600 dark:text-zinc-400 mt-1 text-sm">{description}</p>}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
    </>
  );
}

export function DangerStripe() {
  return <div className="diagonal-stripes h-2 w-full" />;
}

// Shared read-state feedback keeps a failed request distinct from an empty list.
export function RegisterFeedback({ loading, error, onRetry, label = "records" }) {
  if (loading) return <div role="status" className="rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#101620] p-5 text-sm text-zinc-600 dark:text-zinc-300">Loading {label}…</div>;
  if (!error) return null;
  return <div role="alert" className="rounded-xl border border-red-200 dark:border-red-500/30 bg-white dark:bg-[#101620] p-5">
    <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Unable to load {label}.</p>
    <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">Check your connection and try again.</p>
    <button type="button" onClick={onRetry} className="mt-3 min-h-11 px-4 rounded-lg border border-zinc-300 dark:border-white/20 text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FF5F15]">Retry</button>
  </div>;
}
